import { FC, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SheetProductForm } from 'pages/Product/ProductFormSheet';
import { ProductFormValues } from '@libs/schemas';
import { trpc, Product } from 'src/trpc';
import { useToast } from 'components/useToast';

interface EditProductSheetProps {
  product: Product | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditProductSheet: FC<EditProductSheetProps> = ({ product, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { successToast, errorToast } = useToast();

  const { mutate: updateProduct } = trpc.admin.product.updateProduct.useMutation({
    onSuccess: () => {
      successToast({ title: 'Product updated successfully' });
      onClose();
      onSuccess?.();
    },
    onError: () => {
      errorToast({
        title: 'Failed to update product',
        description: 'Failed to update product',
      });
    },
  });

  const { mutate: toggleArchiveMutation } = trpc.admin.product.updateProductArchived.useMutation({
    onSuccess: () => {
      successToast({
        title: 'Product archive status updated successfully',
      });
      onSuccess?.();
    },
    onError: () => {
      errorToast({
        title: 'Failed to update product archive status',
        description: 'Failed to update product archive status',
      });
    },
  });

  const handleSubmit = async (data: ProductFormValues) => {
    if (!product) return;

    setIsLoading(true);
    try {
      updateProduct({
        _id: product._id,
        ...data,
        priceInCents: (Number(data.priceInCents) * 100).toString(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleArchive = async (productId: string) => {
    if (!product) return;

    return toggleArchiveMutation({ _id: productId, archived: !product.archived });
  };

  if (!product) return null;

  const initialData: Partial<ProductFormValues> = {
    label: product.label,
    description: product.description,
    priceInCents: (product.priceInCents / 100).toFixed(2),
    collectTaxes: product.collectTaxes,
    customerVisible: product.customerVisible,
    archived: product.archived,
  };

  return (
    <Sheet open={!!product} onOpenChange={() => onClose()}>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-text-primary">
              Edit Product
            </SheetTitle>
          </SheetHeader>

          <SheetProductForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onToggleArchive={handleToggleArchive}
            isLoading={isLoading}
            productId={product._id}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
